import type { Locale } from './locale';

/**
 * Money is int64 baisa (1 OMR = 1000 baisa) end to end. These are the only
 * functions that turn it into text: 3 decimals, Latin digits, "ر.ع" / "OMR".
 */

export const BAISA_PER_OMR = 1000;

/** The mask shown when the viewer may not see amounts (no f.price.view). */
export const MONEY_MASK = '•••';

export function currencyLabel(locale: Locale): string {
  return locale === 'ar' ? 'ر.ع' : 'OMR';
}

function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** 142000 → "142.000"; -1500 → "-1.500". Exact for any safe integer. */
export function formatAmount(baisa: number): string {
  if (!Number.isFinite(baisa)) return '';
  const v = Math.round(baisa);
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const omr = Math.floor(abs / BAISA_PER_OMR);
  const rest = abs % BAISA_PER_OMR;
  return `${sign}${group(String(omr))}.${String(rest).padStart(3, '0')}`;
}

/** Whole rials, rounded half up: 36000 → "36", 1240500 → "1,241". */
export function formatAmount0(baisa: number): string {
  if (!Number.isFinite(baisa)) return '';
  const omr = Math.round(baisa / BAISA_PER_OMR);
  return (omr < 0 ? '-' : '') + group(String(Math.abs(omr)));
}

/** "142.000 ر.ع" / "142.000 OMR"; null or undefined (masked by the API) gives "•••". */
export function formatMoney(
  baisa: number | null | undefined,
  locale: Locale,
  opts?: { whole?: boolean },
): string {
  if (baisa === null || baisa === undefined) return MONEY_MASK;
  const n = opts?.whole ? formatAmount0(baisa) : formatAmount(baisa);
  return `${n} ${currencyLabel(locale)}`;
}

/**
 * Parses what a person typed as rials ("42.6", "1,250", "٤٢٫٦") into baisa
 * without floating point. Returns null when it is not a valid amount.
 */
export function parseAmount(input: string): number | null {
  const western = input
    .trim()
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.')
    .replace(/[,\s٬]/g, '');
  const m = /^(-)?(\d+)(?:\.(\d{0,3}))?$/.exec(western);
  if (!m) return null;
  const [, neg, whole = '0', frac = ''] = m;
  const v = Number(whole) * BAISA_PER_OMR + Number(frac.padEnd(3, '0'));
  if (!Number.isSafeInteger(v)) return null;
  return neg ? -v : v;
}

/** Basis points as a percent: 3000 → "30%", 1250 → "12.5%". */
export function formatPercentBp(bp: number): string {
  const v = bp / 100;
  return `${Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

/** Integers with Latin digits and thousands separators: 18420 → "18,420". */
export function formatInt(n: number): string {
  if (!Number.isFinite(n)) return '';
  const v = Math.round(n);
  return (v < 0 ? '-' : '') + group(String(Math.abs(v)));
}
